import { notify } from './action.ts';
import { dispatch } from './dispatch.ts';
import { L } from './utils.ts';
import { TrackItem } from './apiSchema.ts';
import {
  filterUnsupportedItems,
  getNextPlayableIndex,
  queueToTrackItem,
  toContainerMediaItem,
} from './playerUtils.ts';
import { configureMusicKit } from './playerActions.ts';

const WAITING_EXPLICIT_TIMER = Number(
  import.meta.env.VITE_WAITING_EXPLICIT_TIMER ?? '5000',
);

// Wait for the next tick to ensure the nowPlayingItem is updated.
const ENDED_TO_QUEUE_UPDATE_DELAY = 100;

/**
 * A notification name indicating that the port is connected.
 */
export const portConnected = () => {
  L.info(`portConnected: Port is connected`);
  dispatch(notify({ type: 'portConnected' }));
};

/**
 * A notification name indicating that the player is ready.
 * @param {boolean} isAuthorized Indicates whether the player is authorized.
 */
export const playerReady = (isAuthorized: boolean) => {
  L.info(`playerReady: Player is ready`);
  dispatch(notify({ type: 'playerReady', value: { isAuthorized } }));
};

enum AuthStatus {
  NOT_DETERMINED,
  DENIED,
  RESTRICTED,
  AUTHORIZED,
}

/**
 * A notification name indicating a change in the authorization status.
 */
const authorizationStatusDidChange = async (
  { authorizationStatus }: { authorizationStatus: number },
) => {
  L.log(
    `authorizationStatusDidChange: Changed the authorization status to ${authorizationStatus}`,
  );

  const player = MusicKit.getInstance();

  await player.clearQueue();
  dispatch(notify({
    type: 'authorizationStatusDidChange',
    value: {
      isAuthorized: authorizationStatus === AuthStatus.AUTHORIZED,
      authorizationStatus,
    },
  }));
};

/**
 * A notification name indicating that DRM is unsupported.
 */
const drmUnsupported = () => {
  L.error(`drmUnsupported: `);
  dispatch(notify({ type: 'drmUnsupported' }));
};

/**
 * A notification name indicating a change in the storefront country code.
 */
const storefrontCountryCodeDidChange = (e: unknown) => {
  const { storefrontCountryCode } = e as {
    storefrontCountryCode: string;
  };
  L.log(
    `storefrontCountryCodeDidChange: Changed the storefront country code to ${storefrontCountryCode}`,
  );
  dispatch(notify({
    type: 'storefrontCountryCodeDidChange',
    value: {
      storefrontCountryCode,
    },
  }));
};

/**
 * A notification name indicating a change in the queue items.
 */
const queueItemsDidChange = (
  mediaItems: unknown, /*: MusicKit.MediaItem[]*/
): void => {
  L.log('queueItemsDidChange: Changed the queue items', mediaItems);

  if (!Array.isArray(mediaItems)) {
    dispatch(notify({
      type: 'queueItemsDidChange',
      value: {
        items: [],
      },
    }));
    return;
  }

  const items = mediaItems as MusicKit.MediaItem[];
  const normalizedItems: TrackItem[] = items.map(queueToTrackItem).filter((
    item,
  ): item is TrackItem => item !== null);

  dispatch(notify({
    type: 'queueItemsDidChange',
    value: {
      items: normalizedItems,
    },
  }));
};

/**
 * A notification name indicating that the queue is ready.
 */
const queueIsReady = () => {
  L.log('queueIsReady');
  dispatch(notify({ type: 'queueIsReady' }));
};

/**
 * A notification name indicating a change in the queue position.
 */
const queuePositionDidChange = (e: unknown) => {
  const { oldPosition, position } = e as {
    oldPosition: number;
    position: number;
  };
  L.log(
    'queuePositionDidChange: Changed the queue position',
    oldPosition,
    position,
  );
  dispatch(notify({
    type: 'queuePositionDidChange',
    value: {
      oldPosition,
      position,
    },
  }));
};

/**
 * A notification name indicating that the media can be played.
 */
const mediaCanPlay = () => {
  L.log('mediaCanPlay: Media can be played');
  dispatch(notify({ type: 'mediaCanPlay' }));
};

/**
 * A notification name indicating a change in the media item state.
 */
const mediaItemStateDidChange = (
  { id, state }: { id: string; state: number },
) => {
  L.log(
    `mediaItemStateDidChange: Changed the media item state=${state}, id=${id}`,
  );
  dispatch(notify({
    type: 'mediaItemStateDidChange',
    value: {
      id,
      state,
    },
  }));
};

/**
 * A notification for indicating that the player has thrown an MKError during playback.
 */
const mediaPlaybackError = (e: unknown) => {
  const { message, errorCode } = e as MusicKit.MKError;
  L.error(`mediaPlaybackError: `, message);
  dispatch(notify({
    type: 'mediaPlaybackError',
    value: {
      message,
      errorCode,
    },
  }));
};

/**
 * A notification for indicating the currently-playing MediaItem has changed.
 */
const nowPlayingItemDidChange = (
  { item }: { item: MusicKit.MediaItem | undefined },
) => {
  L.log('nowPlayingItemDidChange: Changed the now playing item', item);

  if (item) {
    const trackItem = queueToTrackItem(item) as TrackItem;
    const container = toContainerMediaItem(item);

    dispatch(notify({
      type: 'nowPlayingItemDidChange',
      value: {
        item: trackItem,
        container,
      },
    }));
  } else {
    dispatch(notify({
      type: 'nowPlayingItemDidChange',
      value: {
        item: undefined,
        container: undefined,
      },
    }));
  }
};

const playbackStateMap: Record<MusicKit.PlaybackStates, string> = {
  0: 'none',
  1: 'loading',
  2: 'playing',
  3: 'paused',
  4: 'stopped',
  5: 'ended',
  6: 'seeking',
  8: 'waiting',
  9: 'stalled',
  10: 'completed',
};

let contentRestrictedTimer: ReturnType<typeof setTimeout> | null = null;

function clearContentRestrictedTimer(
  timer: ReturnType<typeof setTimeout> | null,
) {
  if (!timer) return;
  clearTimeout(timer);
  contentRestrictedTimer = null;
}

const playbackStateDidChange = (
  { oldState, state }: {
    oldState: MusicKit.PlaybackStates;
    state: MusicKit.PlaybackStates;
  },
) => {
  L.log(
    `playbackStateDidChange: Changed the playback state from ${
      playbackStateMap[oldState]
    } to ${playbackStateMap[state]}`,
  );
  dispatch(notify({
    type: 'playbackStateDidChange',
    value: {
      oldState,
      state,
    },
  }));

  // Checking the content restricted.
  if (
    state === MusicKit.PlaybackStates.ended &&
    (oldState === MusicKit.PlaybackStates.seeking ||
      oldState === MusicKit.PlaybackStates.playing ||
      oldState === MusicKit.PlaybackStates.paused)
  ) {
    clearContentRestrictedTimer(contentRestrictedTimer);

    contentRestrictedTimer = setTimeout(async () => {
      const instance = MusicKit.getInstance();
      const nowPlayingItem = instance.nowPlayingItem;
      if (nowPlayingItem) {
        // If the content is outside the allowed content rating, the nowPlayingItem still contains a value.
        return;
      }
      const nowPlayingItemIndex = instance.nowPlayingItemIndex;
      const items = instance.queue?.items ?? [];
      const item = items[nowPlayingItemIndex];
      const contentRating = item?.contentRating;

      if (contentRating !== 'explicit' || !item) return;

      dispatch(notify({
        type: 'contentRestriction',
        value: {
          item: queueToTrackItem(item) as TrackItem,
        },
      }));

      const isStation = item.container?.type === 'stations';

      await instance.stop();

      if (isStation) {
        const queueItemsDidChangeOnce = async (items: unknown) => {
          if (!items) return;

          const mediaItems = items as MusicKit.MediaItem[];
          instance.removeEventListener(
            'queueItemsDidChange',
            queueItemsDidChangeOnce,
          );

          if (
            instance.nowPlayingItemIndex === -1 ||
            instance.nowPlayingItemIndex === mediaItems.length - 1 ||
            mediaItems[instance.nowPlayingItemIndex]?.contentRating !==
              'explicit' ||
            instance.nowPlayingItem?.container?.id !==
              MusicKit.getInstance().nowPlayingItem?.container?.id
          ) {
            L.warn(
              `[queueItemsDidChangeOnce] The nowPlayingItem is not explicit or the last item, no need to skip.`,
            );
            return;
          }

          // If the content is explicit, we will skip to the next item.
          await instance.skipToNextItem();

          L.info(
            `[queueItemsDidChangeOnce] Skipped to the next item due to content restriction`,
          );
        };

        instance.addEventListener(
          'queueItemsDidChange',
          queueItemsDidChangeOnce,
        );
      } else {
        await instance.skipToNextItem();

        L.info(
          `[PlaybackStates.ended] Skipped to the next item due to content restriction`,
        );
      }
    }, ENDED_TO_QUEUE_UPDATE_DELAY); // Wait for the next tick to ensure the nowPlayingItem is updated.
  } else if (
    state === MusicKit.PlaybackStates.waiting &&
    (oldState === MusicKit.PlaybackStates.playing ||
      oldState === MusicKit.PlaybackStates.paused)
  ) {
    const instance = MusicKit.getInstance();
    const nowPlayingItem = instance.nowPlayingItem;
    const contentRating = nowPlayingItem?.contentRating;

    if (contentRating !== 'explicit' || !nowPlayingItem) return;

    // deno-lint-ignore no-inner-declarations
    function handleMediaCanPlay() {
      clearContentRestrictedTimer(contentRestrictedTimer);
      instance.removeEventListener('mediaCanPlay', handleMediaCanPlay);
    }

    instance.addEventListener('mediaCanPlay', handleMediaCanPlay);

    contentRestrictedTimer = setTimeout(async () => {
      dispatch(notify({
        type: 'contentRestriction',
        value: {
          item: queueToTrackItem(nowPlayingItem!) as TrackItem,
        },
      }));

      await instance.stop();

      if (instance.nowPlayingItemIndex === instance.queue?.items?.length - 1) {
        return; // If the now playing item is the last item, we will not skip to the next item.
      }

      L.info(
        `[media cannot play] Skipped to the next item due to content restriction`,
      );
      // If the content is explicit, we will skip to the next item.
      await instance.skipToNextItem();
    }, WAITING_EXPLICIT_TIMER);
  }
};

const playbackProgressDidChange = async (
  { progress }: { progress: number },
) => {
  dispatch(notify({
    type: 'playbackProgressDidChange',
    value: {
      progress,
    },
  }));

  if (amusic.tokenShouldBeRefreshed && amusic.nextToken) {
    const player = MusicKit.getInstance();
    if (progress >= 0.98 && progress < 1) {
      const items = player.queue?.items!;
      const nowPlayingItemIndex = player.nowPlayingItemIndex;

      // prepare to get the next item index.
      amusic.nextPlayableItemIndex = getNextPlayableIndex(
        items,
        nowPlayingItemIndex,
      );
    }

    if (progress === 1) {
      // If the playback progress is 100%, we will try to configure MusicKit.
      // Before that, we will pause the player to avoid unexpected behavior.
      // Then we will try to backup the current queue and set the next playable item.
      // After configuring MusicKit, we will re-register the player events, set the track queue,
      // and change the media item to the next playable item.
      try {
        await player.pause();

        if (
          amusic.nextPlayableItemIndex !== -1 &&
          amusic.nextPlayableItemIndex !== null
        ) {
          const nextItem = player.queue?.items?.[
            amusic.nextPlayableItemIndex
          ];
          const nextItemId = nextItem?.id;

          amusic.backupQueue =
            player.queue?.items?.filter(filterUnsupportedItems).map((item) => ({
              type: item.type,
              id: item.id,
            })) ?? [];

          await configureMusicKit(amusic.nextToken);

          if (amusic.backupQueue.length === 0) return;

          const newPlayer = MusicKit.getInstance();
          const queue = amusic.backupQueue.map((item) => item.id);
          await newPlayer.setQueue({ songs: queue }); // Support only songs queue
          await newPlayer.changeToMediaAtIndex(
            queue.findIndex((id) => id === nextItemId),
          );
        }
      } catch (error) {
        L.error(
          `playbackProgressDidChange: Error while configure MusicKit: ${
            (error as MusicKit.MKError).message
          }`,
        );

        // MEMO: If the error occurs, we will reset the next playable item index and the backup queue.
        // Retry to configure MusicKit with the next token.
        await configureMusicKit(amusic.nextToken);
      } finally {
        // Reset the next playable item index and the backup queue.
        amusic.tokenShouldBeRefreshed = false;
        amusic.nextToken = null;
        amusic.nextPlayableItemIndex = null;
        amusic.backupQueue = null;
      }
    }
  }
};

const playbackVolumeDidChange = (e: unknown) => {
  const { volume } = e as {
    volume: number;
  };
  L.log(
    `playbackVolumeDidChange: Changed the playback volume`,
  );
  dispatch(notify({
    type: 'playbackVolumeDidChange',
    value: {
      volume,
    },
  }));
};

export function registerPlayerEvents(player: MusicKit.MusicKitInstance) {
  player.addEventListener(
    'authorizationStatusDidChange',
    authorizationStatusDidChange,
  );
  player.addEventListener('drmUnsupported', drmUnsupported);
  player.addEventListener('mediaCanPlay', mediaCanPlay);
  player.addEventListener('mediaItemStateDidChange', mediaItemStateDidChange);
  player.addEventListener('mediaPlaybackError', mediaPlaybackError);
  player.addEventListener('nowPlayingItemDidChange', nowPlayingItemDidChange);
  player.addEventListener('playbackStateDidChange', playbackStateDidChange);
  player.addEventListener(
    'playbackProgressDidChange',
    playbackProgressDidChange,
  );
  player.addEventListener('playbackVolumeDidChange', playbackVolumeDidChange);
  player.addEventListener('queueIsReady', queueIsReady);
  player.addEventListener('queueItemsDidChange', queueItemsDidChange);
  player.addEventListener('queuePositionDidChange', queuePositionDidChange);
  player.addEventListener(
    'storefrontCountryCodeDidChange',
    storefrontCountryCodeDidChange,
  );
}

export function unregisterPlayerEvents(player: MusicKit.MusicKitInstance) {
  player.removeEventListener(
    'authorizationStatusDidChange',
    authorizationStatusDidChange,
  );
  player.removeEventListener('drmUnsupported', drmUnsupported);
  player.removeEventListener('mediaCanPlay', mediaCanPlay);
  player.removeEventListener(
    'mediaItemStateDidChange',
    mediaItemStateDidChange,
  );
  player.removeEventListener('mediaPlaybackError', mediaPlaybackError);
  player.removeEventListener(
    'nowPlayingItemDidChange',
    nowPlayingItemDidChange,
  );
  player.removeEventListener('playbackStateDidChange', playbackStateDidChange);
  player.removeEventListener(
    'playbackProgressDidChange',
    playbackProgressDidChange,
  );
  player.removeEventListener(
    'playbackVolumeDidChange',
    playbackVolumeDidChange,
  );
  player.removeEventListener('queueIsReady', queueIsReady);
  player.removeEventListener('queueItemsDidChange', queueItemsDidChange);
  player.removeEventListener('queuePositionDidChange', queuePositionDidChange);
  player.removeEventListener(
    'storefrontCountryCodeDidChange',
    storefrontCountryCodeDidChange,
  );
}
